// 更新日志 API（反编译 LogBulletinComm + UpdateLogActivity）
//
//   GET https://standard-service.ulucu.com/common/log/bulletin/page?page=N&count=M  av=1
//   GET https://standard-service.ulucu.com/common/log/bulletin/popup?  av=1   （首页弹窗用）
//
// 详情：iOS/Android 用 webview 打开 — 域名按部署 region 切换
//   国内：https://microservice.ulucu.com/support/functionality/updates/{id}?platform=web&token=...
//   海外：https://microservice.apac-ulucu.com/support/functionality/updates/{id}?platform=web&token=...
// 后端 /api/internal/region 端点下发当前部署的 microservice URL。

import axios from 'axios';
import { callInternal } from './internal.js';
import { apiUrl } from '../utils/api-base.js';

/** 反编译 LogBulletinPageEntity.Items */
export interface LogBulletin {
	bulletin_id: string;
	title: string;
	publish_date?: string;     // "2024-02-06"
	create_time?: string;      // "2024-02-05 14:49:23"
	is_unread?: '0' | '1' | string;
	[k: string]: unknown;
}

export interface LogBulletinPage {
	items: LogBulletin[];
	total_count: string;
	current_count: string;
	current_page: string;
	next_page: string;
	prev_page: string;
	page_count: string;
}

const EMPTY: LogBulletinPage = {
	items: [],
	total_count: '0',
	current_count: '0',
	current_page: '1',
	next_page: '',
	prev_page: '',
	page_count: '0',
};

/** 拉更新日志列表 */
export async function fetchChangelogPage(params: { page?: number; count?: number } = {}): Promise<LogBulletinPage> {
	const res = await callInternal<LogBulletinPage>({
		short_name: 'standard',
		action: '/common/log/bulletin/page',
		method: 'GET',
		query: {
			page: params.page ?? 1,
			count: params.count ?? 20,
		},
		av: '1',
	});
	if (res.code !== 0 || !res.data) return EMPTY;
	return { ...EMPTY, ...res.data };
}

/** 拉首页弹窗（最新一条未读） */
export async function fetchChangelogPopup(): Promise<LogBulletin | null> {
	const res = await callInternal<{ items?: LogBulletin[] } | LogBulletin[]>({
		short_name: 'standard',
		action: '/common/log/bulletin/popup',
		method: 'GET',
		av: '1',
	});
	if (res.code !== 0 || !res.data) return null;
	const data = res.data;
	const items = Array.isArray(data) ? data : (data as { items?: LogBulletin[] }).items ?? [];
	return items[0] ?? null;
}

/** /api/internal/region 一次性拉取，整个 session 缓存 */
let _regionCache: { region: 'cn' | 'apac'; microservice: string } | null = null;

async function getRegionMeta(): Promise<{ region: 'cn' | 'apac'; microservice: string }> {
	if (_regionCache) return _regionCache;
	try {
		const res = await axios.get<{ region: 'cn' | 'apac'; microservice: string }>(
			apiUrl('/api/internal/region'),
			{ timeout: 3000 },
		);
		_regionCache = res.data;
	} catch {
		// fallback 到国内域名（向后兼容老后端）
		_regionCache = { region: 'cn', microservice: 'https://microservice.ulucu.com' };
	}
	return _regionCache;
}

/**
 * 详情页 webview 链接（拼接同 Android UpdateLogActivity）
 * 异步：因为要先从后端拿当前部署 region 对应的 microservice 域名
 */
export async function buildChangelogDetailUrl(bulletin_id: string, token: string): Promise<string> {
	const meta = await getRegionMeta();
	return `${meta.microservice}/support/functionality/updates/${bulletin_id}?platform=web&token=${encodeURIComponent(token)}`;
}
